import type {
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community'
import type { ColumnDefinition } from '@/components/atomic/table/types'

import { Badge, BadgeVariant } from '@moment/design-system-ui/badge'

import {
  AVERAGE_RATING_COLUMN,
  DECIMAL_COLUMN,
  DO_NOT_TRADE_STATUS_COLUMN,
  DURATION_COLUMN,
  defaultRatingRenderer,
  MONEY_COLUMN,
  NOTES_COLUMN,
  PERCENTAGE_COLUMN,
  TEXT_COLUMN,
} from '@/components/atomic/table/columns'
import { COLUMN_WIDTH } from '@/components/atomic/table/constants'
import { BodyMediumSubtext } from '@/components/atomic/text'
import { TruncatedTextCell } from '@/components/atomic/truncated-text-cell'
import { AccountIdToggle } from '@/components/molecules/account-id-toggle'
import { ManagementTypeBadge } from '@/components/molecules/badges/management-type'
import { HeldAwayStatusIcon } from '@/components/molecules/held-away-status-icon'
import { MomentStatusIcon } from '@/components/molecules/moment-status-icon'
import { AccountsTableColumnId } from '@/types/enums/accounts'
import {
  defaultValueTransform,
  isNullish,
  PLACEHOLDER,
} from '@/utils/presentation/formatting'

import { MarginEligibilityBadge } from '../badges/margin-eligibility'
import { OrganizationNameCell } from './organization-name-cell'

// Display names for columns in the table headers and settings panel
export const COLUMN_DISPLAY_NAMES: Record<AccountsTableColumnId, string> = {
  [AccountsTableColumnId.MANAGEMENT_TYPE]: 'Type',
  [AccountsTableColumnId.ACCOUNT_NAME]: 'Account Name',
  [AccountsTableColumnId.TRADING_STATUS]: 'Trading',
  [AccountsTableColumnId.ACCOUNT_NOTES]: 'Notes',
  [AccountsTableColumnId.MODEL_NAME]: 'Model',
  [AccountsTableColumnId.MOMENT_STATUS]: 'Status',
  [AccountsTableColumnId.PRIME_BROKERAGE]: 'Prime Brokerage',
  [AccountsTableColumnId.ACCOUNT_STATUS]: 'Account Status',
  [AccountsTableColumnId.ACCOUNT_ID]: 'Account ID',
  [AccountsTableColumnId.MOMENT_ORGANIZATION]: 'Organization',
  [AccountsTableColumnId.ADVISOR_NAME]: 'Advisor',
  [AccountsTableColumnId.TAX_STATE]: 'Tax State',
  [AccountsTableColumnId.FI_ASSETS]: 'FI Assets',
  [AccountsTableColumnId.CASH]: 'Cash',
  [AccountsTableColumnId.TOTAL_MARKET_VALUE]: 'Total Value',
  [AccountsTableColumnId.PERCENT_UNINVESTED_CASH]: '% Uninvested Cash',
  [AccountsTableColumnId.AVG_COUPON]: 'Avg Coupon',
  [AccountsTableColumnId.AVG_YTW]: 'Avg YTW',
  [AccountsTableColumnId.AVG_BOOK_YTW]: 'Avg Book YTW',
  [AccountsTableColumnId.AVG_MATURITY]: 'Avg Maturity',
  [AccountsTableColumnId.AVG_SP_RATING]: 'Avg S&P Rating',
  [AccountsTableColumnId.AVG_DURATION]: 'Avg Duration',
  [AccountsTableColumnId.MONEY_MANAGER]: 'Third Party Manager',
  [AccountsTableColumnId.CUSTODIAN]: 'Custodian',
  [AccountsTableColumnId.ACCOUNT_TYPE]: 'Account Type',
  [AccountsTableColumnId.LAST_ACTIVITY]: 'Last Activity',
  [AccountsTableColumnId.PROPOSAL_STATUS]: 'Proposal Status',
  [AccountsTableColumnId.PORTFOLIO_VALUE]: 'Portfolio Value',
  [AccountsTableColumnId.CASH_BALANCE]: 'Cash Balance',
  [AccountsTableColumnId.PORTFOLIOS]: 'Portfolios',
  [AccountsTableColumnId.MARGIN_ELIGIBILITY]: 'Margin Eligibility',
}

const accountsColumnDefinitions: Map<AccountsTableColumnId, ColumnDefinition> =
  new Map([
    [
      AccountsTableColumnId.MANAGEMENT_TYPE,
      {
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.MANAGEMENT_TYPE],
        field: 'management_type',
        valueFormatter: (params: ValueFormatterParams) => {
          if (isNullish(params.value)) return ''
          return params.value.toString()
        },
        cellRenderer: (params: ICellRendererParams) => {
          if (isNullish(params.value)) {
            if (params.node?.group) {
              return null
            }
            return undefined
          }

          return <ManagementTypeBadge managementType={params.value} />
        },
        minWidth: COLUMN_WIDTH.W_100,
        hide: true,
        colId: AccountsTableColumnId.MANAGEMENT_TYPE,
        sortable: false,
      },
    ],
    [
      AccountsTableColumnId.ACCOUNT_NAME,
      TEXT_COLUMN({
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.ACCOUNT_NAME],
        field: 'account_name',
        colId: AccountsTableColumnId.ACCOUNT_NAME,
        hide: false,
        pinned: 'left',
        sortingOrder: ['asc', 'desc', null],
        cellRenderer: (params: ICellRendererParams) => {
          return <TruncatedTextCell text={params.value} maxLength={18} />
        },
      }),
    ],
    [
      AccountsTableColumnId.ACCOUNT_NOTES,
      NOTES_COLUMN({
        headerName: '',
        field: 'extra',
        colId: AccountsTableColumnId.ACCOUNT_NOTES,
        hide: false,
      }),
    ],
    [
      AccountsTableColumnId.MOMENT_STATUS,
      {
        headerName: 'Status',
        field: 'moment_status',
        cellRenderer: (params: ICellRendererParams) => {
          const statusCell =
            params.value === undefined ? null : (
              <div className="flex items-center justify-center w-full">
                <MomentStatusIcon status={params.value} />
              </div>
            )

          if (params.data?.held_away === true) {
            return <HeldAwayStatusIcon fallback={statusCell} />
          }

          return statusCell
        },
        maxWidth: COLUMN_WIDTH.W_60,
        minWidth: COLUMN_WIDTH.W_60,
        hide: false,
        colId: AccountsTableColumnId.MOMENT_STATUS,
      },
    ],
    [
      AccountsTableColumnId.PRIME_BROKERAGE,
      {
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.PRIME_BROKERAGE],
        field: 'prime_brokerage_enabled',
        cellRenderer: (params: ICellRendererParams) => {
          if (params.value === undefined) {
            return undefined
          }

          const isEnabled = params.value === true

          return (
            <BodyMediumSubtext>
              <Badge
                variant={isEnabled ? BadgeVariant.Success : BadgeVariant.Danger}
              >
                {isEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </BodyMediumSubtext>
          )
        },
        maxWidth: COLUMN_WIDTH.W_140,
        minWidth: COLUMN_WIDTH.W_140,
        hide: true,
        colId: AccountsTableColumnId.PRIME_BROKERAGE,
        sortable: true,
      },
    ],
    [
      AccountsTableColumnId.TRADING_STATUS,
      DO_NOT_TRADE_STATUS_COLUMN({
        headerName: 'Trading',
        field: 'do_not_trade',
        colId: AccountsTableColumnId.TRADING_STATUS,
        sortingOrder: ['asc', 'desc', null],
        hide: false,
      }),
    ],
    [
      AccountsTableColumnId.MODEL_NAME,
      TEXT_COLUMN({
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.MODEL_NAME],
        field: 'model_name',
        colId: AccountsTableColumnId.MODEL_NAME,
        sortable: false,
        hide: false,
        minWidth: COLUMN_WIDTH.W_200,
        cellRenderer: (params: ICellRendererParams) => {
          const sleeves = params.data?.sleeves
          if (!Array.isArray(sleeves) || sleeves.length === 0)
            return PLACEHOLDER

          const modelNames = sleeves
            .map((sleeve) => sleeve.model_assignment?.model?.name)
            .filter((name): name is string => !!name)
          const uniqueModelNames = Array.from(new Set(modelNames))

          if (uniqueModelNames.length === 0) return PLACEHOLDER
          if (uniqueModelNames.length === 1) {
            return (
              <div className="flex items-center w-full">
                {/* biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess */}
                <TruncatedTextCell text={uniqueModelNames[0]!} maxLength={24} />
              </div>
            )
          }
          return <div className="flex items-center w-full">Multiple</div>
        },
      }),
    ],
    [
      AccountsTableColumnId.ACCOUNT_ID,
      TEXT_COLUMN({
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.ACCOUNT_ID],
        field: 'custodian_account_id',
        colId: AccountsTableColumnId.ACCOUNT_ID,
        minWidth: COLUMN_WIDTH.W_160,
        cellRenderer: (params: ICellRendererParams) => {
          if (isNullish(params.value)) return PLACEHOLDER
          return (
            <div>
              <AccountIdToggle
                disableMasking
                accountId={params.value}
                size="small"
              />
            </div>
          )
        },
        cellRendererParams: {
          suppressMouseEventHandling: () => true,
        },
        hide: false,
      }),
    ],
    [
      AccountsTableColumnId.MOMENT_ORGANIZATION,
      TEXT_COLUMN({
        headerName:
          COLUMN_DISPLAY_NAMES[AccountsTableColumnId.MOMENT_ORGANIZATION],
        field: 'moment_organization_id',
        colId: AccountsTableColumnId.MOMENT_ORGANIZATION,
        minWidth: COLUMN_WIDTH.W_120,
        hide: false,
        sortable: false,
        cellRenderer: (params: ICellRendererParams) => {
          return <OrganizationNameCell organizationId={params.value} />
        },
      }),
    ],
    [
      AccountsTableColumnId.ADVISOR_NAME,
      TEXT_COLUMN({
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.ADVISOR_NAME],
        field: 'advisor.name',
        minWidth: COLUMN_WIDTH.W_120,
        colId: AccountsTableColumnId.ADVISOR_NAME,
        sortable: false,
        cellRenderer: (params: ICellRendererParams) => {
          return <TruncatedTextCell text={params.value} />
        },
      }),
    ],
    [
      AccountsTableColumnId.TAX_STATE,
      TEXT_COLUMN({
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.TAX_STATE],
        field: 'tax_state',
        colId: AccountsTableColumnId.TAX_STATE,
        minWidth: COLUMN_WIDTH.W_100,
        maxWidth: COLUMN_WIDTH.W_100,
        hide: false,
      }),
    ],
    [
      AccountsTableColumnId.TOTAL_MARKET_VALUE,
      MONEY_COLUMN({
        headerName:
          COLUMN_DISPLAY_NAMES[AccountsTableColumnId.TOTAL_MARKET_VALUE],
        field: 'statistics.total_market_value',
        colId: AccountsTableColumnId.TOTAL_MARKET_VALUE,
        filter: 'agNumberColumnFilter',
        minWidth: COLUMN_WIDTH.W_160,
      }),
    ],
    [
      AccountsTableColumnId.FI_ASSETS,
      MONEY_COLUMN({
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.FI_ASSETS],
        field: 'statistics.tracked_fixed_income_market_value',
        colId: AccountsTableColumnId.FI_ASSETS,
        filter: 'agNumberColumnFilter',
        hide: false,
      }),
    ],
    [
      AccountsTableColumnId.CASH,
      MONEY_COLUMN({
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.CASH],
        field: 'statistics.cash_market_value',
        colId: AccountsTableColumnId.CASH,
        filter: 'agNumberColumnFilter',
        hide: false,
      }),
    ],
    [
      AccountsTableColumnId.PERCENT_UNINVESTED_CASH,
      PERCENTAGE_COLUMN(
        {
          headerName:
            COLUMN_DISPLAY_NAMES[AccountsTableColumnId.PERCENT_UNINVESTED_CASH],
          field: 'statistics.percent_uninvested_cash',
          colId: AccountsTableColumnId.PERCENT_UNINVESTED_CASH,
          minWidth: COLUMN_WIDTH.W_160,
          hide: false,
        },
        { numDigits: 2 },
      ),
    ],
    [
      AccountsTableColumnId.AVG_COUPON,
      PERCENTAGE_COLUMN(
        {
          headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.AVG_COUPON],
          field: 'statistics.average_coupon',
          colId: AccountsTableColumnId.AVG_COUPON,
          minWidth: COLUMN_WIDTH.W_120,
          hide: false,
        },
        { numDigits: 2 },
      ),
    ],
    [
      AccountsTableColumnId.AVG_YTW,
      PERCENTAGE_COLUMN(
        {
          headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.AVG_YTW],
          field: 'statistics.average_ytw',
          colId: AccountsTableColumnId.AVG_YTW,
          minWidth: COLUMN_WIDTH.W_120,
          hide: false,
        },
        { numDigits: 3 },
      ),
    ],
    [
      AccountsTableColumnId.AVG_BOOK_YTW,
      PERCENTAGE_COLUMN(
        {
          headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.AVG_BOOK_YTW],
          field: 'statistics.average_book_ytw',
          colId: AccountsTableColumnId.AVG_BOOK_YTW,
          minWidth: COLUMN_WIDTH.W_140,
          hide: false,
        },
        { numDigits: 3 },
      ),
    ],
    [
      AccountsTableColumnId.AVG_MATURITY,
      DECIMAL_COLUMN(
        {
          headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.AVG_MATURITY],
          field: 'statistics.average_maturity_in_years',
          colId: AccountsTableColumnId.AVG_MATURITY,
          minWidth: COLUMN_WIDTH.W_120,
          hide: false,
        },
        { numDigits: 3 },
      ),
    ],
    [
      AccountsTableColumnId.AVG_SP_RATING,
      AVERAGE_RATING_COLUMN({
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.AVG_SP_RATING],
        field: '', // Cell renderer responsible for accessing the correct field.
        colId: AccountsTableColumnId.AVG_SP_RATING,
        minWidth: COLUMN_WIDTH.W_120,
        hide: false,
        cellRendererParams: {
          isAverageRating: true,
        },
        cellRenderer: (params: ICellRendererParams) =>
          defaultRatingRenderer({
            ...params,
            data: {
              ...params.data,
              average_sp_rating: params.data?.statistics?.average_sp_rating,
              average_moodys_rating:
                params.data?.statistics?.average_moodys_rating,
            },
          }),
      }),
    ],
    [
      AccountsTableColumnId.AVG_DURATION,
      DURATION_COLUMN({
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.AVG_DURATION],
        field: 'statistics.average_duration',
        colId: AccountsTableColumnId.AVG_DURATION,
        minWidth: COLUMN_WIDTH.W_120,
        hide: false,
      }),
    ],
    [
      AccountsTableColumnId.MONEY_MANAGER,
      TEXT_COLUMN({
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.MONEY_MANAGER],
        field: 'extra.money_manager',
        minWidth: COLUMN_WIDTH.W_120,
        colId: AccountsTableColumnId.MONEY_MANAGER,
        sortable: false,
        cellRenderer: (params: ICellRendererParams) => {
          return <TruncatedTextCell text={params.value} />
        },
      }),
    ],
    [
      AccountsTableColumnId.CUSTODIAN,
      TEXT_COLUMN({
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.CUSTODIAN],
        field: 'custodian_id',
        colId: AccountsTableColumnId.CUSTODIAN,
        minWidth: COLUMN_WIDTH.W_120,
        hide: false,
        cellRenderer: (params: ICellRendererParams) => {
          if (isNullish(params.value)) return PLACEHOLDER

          const custodianId = params.data?.custodian.name
          return <TruncatedTextCell text={defaultValueTransform(custodianId)} />
        },
      }),
    ],
    [
      AccountsTableColumnId.ACCOUNT_TYPE,
      TEXT_COLUMN({
        headerName: COLUMN_DISPLAY_NAMES[AccountsTableColumnId.ACCOUNT_TYPE],
        field: 'account_type',
        colId: AccountsTableColumnId.ACCOUNT_TYPE,
        minWidth: COLUMN_WIDTH.W_120,
        hide: false,
        cellRenderer: (params: ICellRendererParams) => {
          return <TruncatedTextCell text={params.value} />
        },
      }),
    ],
    [
      AccountsTableColumnId.MARGIN_ELIGIBILITY,
      {
        headerName:
          COLUMN_DISPLAY_NAMES[AccountsTableColumnId.MARGIN_ELIGIBILITY],
        field: 'margin_eligible',
        cellRenderer: (params: ICellRendererParams) => {
          const isEligible = !!params.value

          return <MarginEligibilityBadge eligible={isEligible} />
        },
        minWidth: COLUMN_WIDTH.W_160,
        hide: false,
        colId: AccountsTableColumnId.MARGIN_ELIGIBILITY,
        sortable: false,
      },
    ],
  ])

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default accountsColumnDefinitions
